/**
 * TierConfirmation — issue #24 (Becky-T1.1).
 *
 * Surfaces a tier-selection prompt when staff click "Check Out — Grooming"
 * on a grooming booking. Defaults to the booked tier; lets the operator
 * change it. Tier-change is logged as an audit entry (currently local
 * stop-gap; server endpoint follow-on PR).
 *
 * Direct fix for the Yogi-class undercharging seen during Becky's
 * 2026-05-11 on-site walkthrough:
 *   - Customer booked a "basic cut"
 *   - Jorge gave a "profile cut"
 *   - Becky charged at the booked tier — lost revenue
 *
 * **Tier definitions are placeholder defaults** — Ted/Julian/Jorge to
 * finalize the actual numbers post-deploy. See `lib/groomingTiers.ts`.
 *
 * Server-side note: client-side recalculated total is a PREVIEW for the
 * operator. The server MUST recalc on submit to prevent client tampering.
 * That's the responsibility of the `/check-out` endpoint (Track-A); the
 * client just passes the chosen tier key.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DEFAULT_GROOMING_TIERS,
  computeTierAdjustedTotalCents,
  findGroomingTier,
  resolveGroomingTiers,
  type GroomingTierDefinition,
  type GroomingTierKey,
} from '../../lib/groomingTiers.js';
import type { CheckoutAction } from './CheckoutActions.js';
import {
  RequestedAddOns,
  selectedRequestedAddOnsCents,
  type RequestedAddOnsControl,
} from './RequestedAddOns.js';
import './TierConfirmation.css';

export interface TierConfirmationProps {
  /** Whether the modal is open. Parent controls visibility. */
  readonly open: boolean;
  /** Close the modal without submitting. */
  readonly onClose: () => void;
  /**
   * Called when staff confirm. Parent is responsible for the actual
   * server call + audit-log append + booking-list update. The
   * `checkoutAction` discriminator routes to settle-now (existing
   * payment flow) vs settle-later (queue an open invoice) — issue #25.
   */
  readonly onConfirm: (args: {
    readonly selectedTier: GroomingTierDefinition;
    readonly originalTier: GroomingTierDefinition;
    readonly finalTotalCents: number;
    readonly operatorNotes: string | null;
    readonly checkoutAction: CheckoutAction;
  }) => Promise<void> | void;
  /** Display label for the booking (e.g. "10:30 · Yogi Smith"). */
  readonly bookingLabel: string;
  /** Existing total at the time of checkout, in cents. */
  readonly existingTotalCents: number;
  /**
   * The tier the booking was originally created at. If we can't
   * determine it (booking predates tier tracking), defaults to `basic`.
   */
  readonly originalTierKey?: GroomingTierKey | null;
  /**
   * Tenant-specific tier definitions, if available. Falls back to defaults.
   */
  readonly tierOverride?: ReadonlyArray<GroomingTierDefinition> | null;
  /** Disable submit while parent is processing. */
  readonly submitting?: boolean;
  // Sawyer 13 household-checkout-grooming (C3)
  /**
   * 'single' (default) is the row's own grooming checkout, byte-for-byte as
   * before. 'household-step' is one groom inside "Check out all": the footer
   * is Cancel + one primary that confirms the tier and moves on (the family
   * pays once in the settle dialog after the last step), and `onConfirm`
   * always receives `checkoutAction: 'settle-now'`.
   */
  readonly mode?: 'single' | 'household-step';
  /** household-step only: which groom of how many, and its dog. */
  readonly step?: { readonly index: number; readonly count: number; readonly dogLabel: string };
  /**
   * The customer's requested add-ons for this groom. Omitted / null ⇒ no
   * section (a non-grooming caller, or none loaded). The tier total passed to
   * `onConfirm` NEVER includes them: each is billed once, as its own leg.
   */
  readonly requestedAddOns?: RequestedAddOnsControl | null;
}

function formatMoney(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

export function TierConfirmation({
  open,
  onClose,
  onConfirm,
  bookingLabel,
  existingTotalCents,
  originalTierKey,
  tierOverride,
  submitting = false,
  mode = 'single',
  step,
  requestedAddOns = null,
}: TierConfirmationProps): JSX.Element | null {
  const tiers = useMemo(
    () => resolveGroomingTiers(tierOverride),
    [tierOverride],
  );

  const originalTier = useMemo<GroomingTierDefinition>(() => {
    return (
      findGroomingTier(originalTierKey, tiers) ??
      tiers.find((t) => t.key === 'basic') ??
      DEFAULT_GROOMING_TIERS[0]!
    );
  }, [originalTierKey, tiers]);

  const [selectedKey, setSelectedKey] = useState<GroomingTierKey>(
    originalTier.key,
  );
  const [operatorNotes, setOperatorNotes] = useState<string>('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLFormElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  // Reset state when the modal opens (so a stale selection from a prior
  // booking doesn't leak).
  useEffect(() => {
    if (open) {
      setSelectedKey(originalTier.key);
      setOperatorNotes('');
      setSubmitError(null);
    }
  }, [open, originalTier.key]);

  useEffect(() => {
    if (!open) return undefined;
    restoreFocusRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    closeRef.current?.focus();
    return () => restoreFocusRef.current?.focus();
  }, [open]);

  const selectedTier = useMemo<GroomingTierDefinition>(() => {
    return (
      findGroomingTier(selectedKey, tiers) ?? originalTier
    );
  }, [selectedKey, tiers, originalTier]);

  const finalTotalCents = useMemo(() => {
    return computeTierAdjustedTotalCents({
      baseTier: originalTier,
      selectedTier,
      existingTotalCents,
    });
  }, [originalTier, selectedTier, existingTotalCents]);

  const tierChanged = selectedTier.key !== originalTier.key;
  const priceDeltaCents = finalTotalCents - existingTotalCents;
  // Sawyer C3 (refuter 3) — display only: the ticked add-ons are billed as
  // their OWN lines, so they never enter `finalTotalCents`.
  const addOnsCents = selectedRequestedAddOnsCents(requestedAddOns);
  const householdStep = mode === 'household-step';
  const lastStep = !step || step.index >= step.count;

  const handleSubmit = useCallback(
    async (checkoutAction: CheckoutAction) => {
      setSubmitError(null);
      try {
        await onConfirm({
          selectedTier,
          originalTier,
          finalTotalCents,
          operatorNotes: operatorNotes.trim() ? operatorNotes.trim() : null,
          checkoutAction,
        });
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Could not finalize check-out';
        setSubmitError(message);
      }
    },
    [onConfirm, selectedTier, originalTier, finalTotalCents, operatorNotes],
  );

  const handleDialogKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLFormElement>): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        if (!submitting) onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable || focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [onClose, submitting],
  );

  if (!open) return null;

  return (
    <div
      className="ferro-tier-confirm__backdrop"
      data-testid="tier-confirm-overlay"
    >
      <button
        type="button"
        aria-label="Close tier confirmation"
        data-testid="tier-confirm-backdrop"
        onClick={onClose}
        disabled={submitting}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          padding: 0,
          border: 0,
          background: 'transparent',
          cursor: 'default',
        }}
      />
      <form
        ref={dialogRef}
        className="ferro-tier-confirm"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ferro-tier-confirm-title"
        onKeyDown={handleDialogKeyDown}
        onSubmit={(e) => {
          e.preventDefault();
          // Form submit defaults to settle-now (Enter key from any field).
          void handleSubmit('settle-now');
        }}
        data-testid="tier-confirm-form"
        style={{ position: 'relative' }}
      >
        <header className="ferro-tier-confirm__header">
          <h2 id="ferro-tier-confirm-title" className="ferro-tier-confirm__title">
            Confirm grooming tier
          </h2>
          <button
            type="button"
            ref={closeRef}
            className="ferro-tier-confirm__close"
            onClick={onClose}
            aria-label="Close"
            disabled={submitting}
          >
            ×
          </button>
        </header>

        <p className="ferro-tier-confirm__subtitle">
          <span className="ferro-tier-confirm__booking-label">{bookingLabel}</span>
        </p>
        {householdStep && step ? (
          <p className="ferro-tier-confirm__subtitle" data-testid="tier-confirm-step">
            {step.dogLabel} · grooming visit {step.index} of {step.count} in this checkout
          </p>
        ) : null}

        <p className="ferro-tier-confirm__intro">
          The booking was created at <strong>{originalTier.label}</strong>. Confirm
          the tier the dog actually got — pricing updates automatically. Server
          re-calculates final total on submit.
        </p>

        <fieldset
          className="ferro-tier-confirm__tiers"
          data-testid="tier-confirm-tiers"
        >
          <legend className="ferro-tier-confirm__legend">Tier</legend>
          {tiers.map((tier) => {
            const isSelected = tier.key === selectedKey;
            const isOriginal = tier.key === originalTier.key;
            return (
              <label
                key={tier.key}
                htmlFor={`tier-confirm-${tier.key}`}
                className={`ferro-tier-confirm__tier${isSelected ? ' ferro-tier-confirm__tier--selected' : ''}`}
                data-testid={`tier-option-${tier.key}`}
              >
                <input
                  id={`tier-confirm-${tier.key}`}
                  type="radio"
                  name="grooming-tier"
                  value={tier.key}
                  checked={isSelected}
                  disabled={submitting}
                  onChange={() => setSelectedKey(tier.key)}
                  data-testid={`tier-radio-${tier.key}`}
                />
                <span className="ferro-tier-confirm__tier-body">
                  <span className="ferro-tier-confirm__tier-head">
                    <span className="ferro-tier-confirm__tier-label">
                      {tier.label}
                      {isOriginal ? (
                        <span
                          className="ferro-tier-confirm__tier-badge"
                          aria-label="Booked tier"
                        >
                          Booked
                        </span>
                      ) : null}
                    </span>
                    <span className="ferro-tier-confirm__tier-price">
                      {formatMoney(tier.basePriceCents)}
                    </span>
                  </span>
                  <span className="ferro-tier-confirm__tier-description">
                    {tier.description}
                  </span>
                  <span className="ferro-tier-confirm__tier-duration">
                    ~{tier.durationMinutes} min
                  </span>
                </span>
              </label>
            );
          })}
        </fieldset>

        <label className="ferro-tier-confirm__notes" htmlFor="ferro-tier-notes">
          <span className="ferro-tier-confirm__notes-label">Notes (optional)</span>
          <textarea
            id="ferro-tier-notes"
            className="ferro-tier-confirm__notes-input"
            rows={2}
            value={operatorNotes}
            onChange={(e) => setOperatorNotes(e.target.value)}
            disabled={submitting}
            placeholder="Why the tier was adjusted, special requests, etc."
            data-testid="tier-confirm-notes"
            maxLength={500}
          />
        </label>

        {requestedAddOns ? <RequestedAddOns control={requestedAddOns} disabled={submitting} /> : null}

        <section
          className="ferro-tier-confirm__summary"
          aria-label="Pricing summary"
          data-testid="tier-confirm-summary"
        >
          <dl>
            <div>
              <dt>Booked total</dt>
              <dd>{formatMoney(existingTotalCents)}</dd>
            </div>
            {tierChanged ? (
              <div className="ferro-tier-confirm__summary-delta">
                <dt>
                  {originalTier.label} → {selectedTier.label}
                </dt>
                <dd>
                  {priceDeltaCents >= 0 ? '+' : ''}
                  {formatMoney(priceDeltaCents)}
                </dd>
              </div>
            ) : null}
            <div className="ferro-tier-confirm__summary-total">
              <dt>New total</dt>
              <dd data-testid="tier-confirm-total">
                {formatMoney(finalTotalCents)}
              </dd>
            </div>
            {addOnsCents > 0 ? (
              <>
                <div className="ferro-tier-confirm__summary-delta">
                  <dt>Add-ons (billed as their own lines)</dt>
                  <dd data-testid="tier-confirm-addons-total">+{formatMoney(addOnsCents)}</dd>
                </div>
                <div className="ferro-tier-confirm__summary-total">
                  <dt>Charged today</dt>
                  <dd data-testid="tier-confirm-charged-today">{formatMoney(finalTotalCents + addOnsCents)}</dd>
                </div>
              </>
            ) : null}
          </dl>
        </section>

        {submitError ? (
          <p
            className="ferro-form__error"
            role="alert"
            data-testid="tier-confirm-error"
          >
            {submitError}
          </p>
        ) : null}

        {householdStep ? (
          <footer className="ferro-tier-confirm__footer">
            <button
              type="button"
              className="ferro-btn ferro-btn--ghost"
              onClick={onClose}
              disabled={submitting}
              data-testid="tier-confirm-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="ferro-btn ferro-btn--primary"
              disabled={submitting}
              data-testid="tier-confirm-step-submit"
            >
              {lastStep ? 'Confirm tier and check out all' : 'Confirm tier — next grooming visit'}
            </button>
          </footer>
        ) : (
        <footer className="ferro-tier-confirm__footer">
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={onClose}
            disabled={submitting}
            data-testid="tier-confirm-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={() => void handleSubmit('settle-later')}
            disabled={submitting}
            data-testid="tier-confirm-settle-later"
          >
            {submitting ? 'Submitting…' : 'Confirm — Settle Later'}
          </button>
          <button
            type="submit"
            className="ferro-btn ferro-btn--primary"
            disabled={submitting}
            data-testid="tier-confirm-submit"
          >
            {submitting ? 'Submitting…' : 'Confirm & Settle Now'}
          </button>
        </footer>
        )}
      </form>
    </div>
  );
}
